import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import "./MtdPerformanceReport.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COLUMNS = [
  { key: "name", label: "Dealership" },
  { key: "budget", label: "Budget" },
  { key: "spent", label: "Spent" },
  { key: "budgetPace", label: "Budget Pace" },
  { key: "totalSpent", label: "Total Spent" },
  { key: "leadGoal", label: "Lead Goal" },
  { key: "leads", label: "Leads" },
  { key: "totalLeads", label: "Total Leads" },
  { key: "leadsPercent", label: "Leads %" },
  { key: "cplGoal", label: "CPL Goal" },
  { key: "cpl", label: "CPL" },
  { key: "callGoal", label: "Call Goal" },
  { key: "calls", label: "Calls" },
  { key: "callsPercent", label: "Calls %" },
  { key: "costPerCall", label: "Cost/Call" }
];

const getMetricValue = (row, keys, defaultValue = undefined) => {
  if (!row) return defaultValue;
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  return defaultValue;
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toTargetNumber = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const normalizeStoreKey = (value) => String(value || "").trim().toLowerCase();

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString();
};

const formatNumberZero = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatNumberOne = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${num.toFixed(1)}%`;
};


const getStoreName = (row) => {
  return (
    row?.store_name ||
    row?.STORE_NAME ||
    row?.account_name ||
    row?.ACCOUNT_NAME ||
    row?.store ||
    row?.dealership ||
    row?.name ||
    row?.storeName ||
    row?.store_id ||
    row?.id ||
    "-"
  );
};

const getAdsMetric = (row, keys) => toNumber(getMetricValue(row, keys, 0));

const usedDivisions = [
  "DriveNation",
  "FFUN Cars",
  "FFUN Cars Saskatoon",
  "Used",
  "Carooga",
  "FFUN Group"
];

const usedStoreNames = ["Carooga", "FFUN Group"];

const normalizeDivision = (divisionValue) => {
  const division = (divisionValue || "").trim();
  if (!division) return "";
  if (usedDivisions.includes(division)) return "Used";
  return "OEM";
};

export default function MtdPerformanceReport() {
  const [stores, setStores] = useState([]);
  const [rows, setRows] = useState([]);
  const [targetsByStore, setTargetsByStore] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState(new Set(COLUMNS.map((col) => col.key)));
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const storeMenuRef = useRef(null);
  const columnMenuRef = useRef(null);
  const hasInitializedStores = useRef(false);

  const startDate = useMemo(() => moment().startOf("month"), []);
  const endDate = useMemo(() => {
    const yesterday = moment().subtract(1, "day");
    return yesterday.isBefore(startDate) ? startDate.clone() : yesterday;
  }, [startDate]);

  const totalDays = endDate.daysInMonth();
  const elapsedDays = endDate.date();
  const paceFactor = totalDays ? elapsedDays / totalDays : 0;

  const dateLabel = `${startDate.format("MMM D")} - ${endDate.format("D, YYYY")}`;
  const targetMonth = startDate.format("YYYY-MM");

  const fetchRows = () => {
    setLoading(true);
    setError("");

    const payload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };

    axios
      .post(Links.googleAdsDivisionsDaily, payload)
      .then((res) => {
        setRows(res.data?.data || res.data || []);
      })
      .catch((err) => {
        console.log("MTD performance fetch error:", err);
        setRows([]);
        setError("Failed to load MTD performance data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    axios
      .get(Links.getAllStores)
      .then((res) => {
        setStores(res.data?.stores || []);
      })
      .catch((err) => {
        console.log("Stores fetch error:", err);
        setStores([]);
      });
  }, []);

  useEffect(() => {
    if (rows.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    const storeDivisionPairs = new Map();
    rows.forEach((row) => {
      const storeName = getStoreName(row);
      if (!storeName || storeName === "-") return;
      const storeKey = normalizeStoreKey(storeName);
      if (!storeDivisionPairs.has(storeKey)) {
        storeDivisionPairs.set(storeKey, {
          name: storeName,
          division: getDivisionGroup(row)
        });
      }
    });

    const entries = Array.from(storeDivisionPairs.entries());
    if (entries.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    Promise.allSettled(
      entries.map(([, meta]) =>
        axios.post(Links.marketingTargets, {
          division: meta.division,
          dealer: meta.name,
          month: targetMonth
        })
      )
    )
      .then((results) => {
        const next = new Map();
        results.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const storeKey = entries[index][0];
          const payload = result.value?.data?.data ?? result.value?.data ?? {};
          const targetRow = Array.isArray(payload) ? payload[0] : payload;
          if (!targetRow) return;

          const budget = toTargetNumber(
            getMetricValue(targetRow, ["google_ads_budget", "budget", "googleAdsBudget"])
          );
          const leadGoal = toTargetNumber(
            getMetricValue(targetRow, ["google_ads_leads_goal", "leads_goal", "googleAdsLeadsGoal"])
          );
          const callGoal = toTargetNumber(
            getMetricValue(targetRow, ["google_ads_calls_goal", "calls_goal", "googleAdsCallsGoal"])
          );

          if (budget !== undefined || leadGoal !== undefined || callGoal !== undefined) {
            next.set(storeKey, { budget, leadGoal, callGoal });
          }
        });
        setTargetsByStore(next);
      })
      .catch((err) => {
        console.log("MTD targets fetch error:", err);
        setTargetsByStore(new Map());
      });
  }, [rows, targetMonth]);

  const storeLookup = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    stores.forEach((store) => {
      if (store?.id !== undefined && store?.id !== null) {
        byId.set(String(store.id), store);
      }
      if (store?.name) {
        byName.set(String(store.name).trim().toLowerCase(), store);
      }
    });
    return { byId, byName };
  }, [stores]);

  const getStoreMeta = (row) => {
    const rowStoreId = row?.store_id ?? row?.STORE_ID ?? row?.storeId ?? row?.id;
    if (rowStoreId !== undefined && rowStoreId !== null) {
      const byId = storeLookup.byId.get(String(rowStoreId));
      if (byId) return byId;
    }
    const name = getStoreName(row);
    if (!name) return null;
    return storeLookup.byName.get(String(name).trim().toLowerCase()) || null;
  };

  const getRowDivisionLabel = (row) => {
    const directDivision = (row?.division || row?.store_division || row?.storeDivision || row?.Division || "").trim();
    if (directDivision) return directDivision;
    const meta = getStoreMeta(row);
    return (meta?.division || "").trim();
  };

  const getDivisionGroup = (row) => {
    const divisionLabel = getRowDivisionLabel(row);
    const normalized = normalizeDivision(divisionLabel);
    if (normalized) return normalized;

    const storeName = getStoreName(row);
    if (storeName) {
      const normalizedStore = storeName.trim().toLowerCase();
      const isUsedStore = usedStoreNames.some(
        (name) => name.toLowerCase() === normalizedStore || normalizedStore.includes(name.toLowerCase())
      );
      if (isUsedStore) return "Used";
    }

    return "OEM";
  };

  const buildDerivedMetrics = (entry) => {
    const leadGoal = entry.leadGoal || 0;
    const cplGoal = leadGoal ? entry.budget / leadGoal : 0;
    const cpl = entry.leads ? entry.spent / entry.leads : 0;
    const leadsPercent = leadGoal ? (entry.leads / leadGoal) * 100 : 0;
    const callsPercent = entry.callGoal ? (entry.calls / entry.callGoal) * 100 : 0;
    const costPerCall = entry.calls ? entry.spent / entry.calls : 0;
    const paceTarget = entry.budget * paceFactor;
    const pacePercent = paceTarget ? ((entry.spent / paceTarget) - 1) * 100 : 0;

    return {
      ...entry,
      leadGoal,
      cplGoal,
      cpl,
      leadsPercent,
      callsPercent,
      costPerCall,
      pacePercent,
      totalSpent: entry.spent,
      totalLeads: entry.leads
    };
  };

  const aggregatedRows = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const store = getStoreName(row);
      if (!map.has(store)) {
        map.set(store, {
          store,
          divisionGroup: getDivisionGroup(row),
          budget: 0,
          spent: 0,
          leadGoal: 0,
          leads: 0,
          cplGoal: 0,
          callGoal: 0,
          calls: 0
        });
      }

      const entry = map.get(store);
      if (!entry.divisionGroup) {
        entry.divisionGroup = getDivisionGroup(row);
      }
      entry.budget += getAdsMetric(row, [
        "BUDGET",
        "budget",
        "monthly_budget",
        "budget_amount",
        "budget_total"
      ]);
      entry.spent += getAdsMetric(row, ["SPEND", "spend", "cost", "amount_spent"]);
      entry.leads += getAdsMetric(row, ["LEADS", "leads", "lead"]);
      entry.calls += getAdsMetric(row, ["CALLS", "calls", "phone_calls", "call", "phone_call"]);

      const leadGoalValue = getAdsMetric(row, ["LEAD_GOAL", "lead_goal", "leads_goal", "goal_leads", "leads_target"]);
      const callGoalValue = getAdsMetric(row, ["CALL_GOAL", "call_goal", "calls_goal", "calls_target"]);

      entry.leadGoal += leadGoalValue;
      entry.callGoal += callGoalValue;
    });

    const withTargets = Array.from(map.values()).map((entry) => {
      const targets = targetsByStore.get(normalizeStoreKey(entry.store));
      if (targets) {
        if (targets.budget !== undefined) entry.budget = targets.budget;
        if (targets.leadGoal !== undefined) entry.leadGoal = targets.leadGoal;
        if (targets.callGoal !== undefined) entry.callGoal = targets.callGoal;
      }
      return entry;
    });

    return withTargets.map((entry) => buildDerivedMetrics(entry));
  }, [rows, paceFactor, targetsByStore]);

  useEffect(() => {
    if (hasInitializedStores.current || aggregatedRows.length === 0) return;
    setSelectedStores(new Set(aggregatedRows.map((row) => row.store)));
    hasInitializedStores.current = true;
  }, [aggregatedRows]);

  const filteredRows = useMemo(() => {
    if (selectedStores.size === 0) return [];
    return aggregatedRows.filter((row) => selectedStores.has(row.store));
  }, [aggregatedRows, selectedStores]);

  const calculateTotals = (items) => {
    const totals = items.reduce(
      (acc, row) => {
        acc.budget += row.budget;
        acc.spent += row.spent;
        acc.leadGoal += row.leadGoal;
        acc.leads += row.leads;
        acc.cplGoal += row.cplGoal;
        acc.callGoal += row.callGoal;
        acc.calls += row.calls;
        return acc;
      },
      { budget: 0, spent: 0, leadGoal: 0, leads: 0, cplGoal: 0, callGoal: 0, calls: 0 }
    );

    return buildDerivedMetrics({ store: "Total", ...totals });
  };

  const sections = useMemo(() => {
    const oemRows = filteredRows.filter((row) => row.divisionGroup !== "Used");
    const usedRows = filteredRows.filter((row) => row.divisionGroup === "Used");
    return [
      { key: "OEM DIVISION", rows: oemRows },
      { key: "USED DIVISION", rows: usedRows }
    ];
  }, [filteredRows]);

  useEffect(() => {
    if (!storeMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (storeMenuRef.current && !storeMenuRef.current.contains(event.target)) {
        setStoreMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [storeMenuOpen]);

  useEffect(() => {
    if (!columnMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setColumnMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [columnMenuOpen]);

  const storeGroups = useMemo(() => {
    const groups = {
      "OEM Division": [],
      "Used Division": []
    };
    aggregatedRows.forEach((row) => {
      const key = row.divisionGroup === "Used" ? "Used Division" : "OEM Division";
      groups[key].push(row.store);
    });
    return groups;
  }, [aggregatedRows]);

  const visibleColumnList = useMemo(
    () => COLUMNS.filter((col) => visibleColumns.has(col.key)),
    [visibleColumns]
  );

  const storeLabel = useMemo(() => {
    if (selectedStores.size === 0) return "None selected";
    if (selectedStores.size === aggregatedRows.length && aggregatedRows.length > 0) return "All Dealerships";
    if (selectedStores.size <= 3) return Array.from(selectedStores).join(", ");
    return `${selectedStores.size} dealerships selected`;
  }, [selectedStores, aggregatedRows.length]);

  const columnLabel = useMemo(() => {
    const toggleableCount = COLUMNS.length - 1;
    const shownCount = visibleColumns.has("name") ? visibleColumns.size - 1 : visibleColumns.size;
    if (shownCount === toggleableCount) return "All Columns";
    if (shownCount <= 0) return "Name only";
    return `${shownCount} of ${toggleableCount} columns`;
  }, [visibleColumns]);

  const leadsChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const pacedGoals = filteredRows.map((row) =>
      row.leadGoal ? Number((row.leadGoal * paceFactor).toFixed(1)) : 0
    );
    const achieved = filteredRows.map((row) => row.leads || 0);
    const achievedColors = filteredRows.map((row, index) => {
      const goal = pacedGoals[index];
      if (!goal) return "#808284";
      const ratio = achieved[index] / goal;
      if (ratio >= 1) return "#329044";
      if (ratio >= 0.8) return "#FFC62C";
      return "#EF3742";
    });

    return {
      labels,
      datasets: [
        {
          label: "Paced Goal",
          data: pacedGoals,
          backgroundColor: "rgba(65,64,66,0.18)",
          borderColor: "rgba(65,64,66,0.55)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        },
        {
          label: "Achieved",
          data: achieved,
          backgroundColor: achievedColors.map((color) => {
            const hex = color.replace("#", "");
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return `rgba(${r},${g},${b},0.82)`;
          }),
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        }
      ]
    };
  }, [filteredRows, paceFactor]);

  const leadsChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { family: "Rubik", size: 11 }, boxWidth: 14 }
        },
        datalabels: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x.toFixed(1)} leads`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { font: { family: "Rubik", size: 11 } },
          title: { display: true, text: "Leads", font: { family: "Rubik", size: 12 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const cplChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const cplValues = filteredRows.map((row) => row.cpl || 0);
    const cplGoals = filteredRows.map((row) => row.cplGoal || 0);

    return {
      labels,
      datasets: [
        {
          label: "CPL",
          data: cplValues,
          backgroundColor: "#F78E20",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75,
          order: 2
        },
        {
          label: "CPL Goal",
          data: cplGoals,
          backgroundColor: "rgba(65,64,66,0.2)",
          borderColor: "#414042",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75,
          order: 1,
          type: "bar"
        }
      ]
    };
  }, [filteredRows]);

  const cplChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { family: "Rubik", size: 11 }, boxWidth: 14 }
        },
        datalabels: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.x.toFixed(2)}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { callback: (value) => `$${value}`, font: { family: "Rubik", size: 11 } },
          title: { display: true, text: "Cost Per Lead ($)", font: { family: "Rubik", size: 12 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const leadPctClass = (value) => {
    if (value === null || value === undefined) return "";
    if (value >= 80) return "clr-green";
    if (value >= 60) return "clr-yellow";
    return "clr-red";
  };

  const cplClass = (current, goal) => {
    if (!current || !goal) return "";
    const ratio = current / goal;
    if (ratio <= 1) return "clr-green";
    if (ratio <= 1.15) return "clr-yellow";
    return "clr-red";
  };

  const paceClass = (value) => {
    if (value === null || value === undefined) return "";
    const abs = Math.abs(value);
    if (abs <= 10) return "clr-green";
    if (abs <= 25) return "clr-yellow";
    return "clr-red";
  };

  const formatPace = (value) => {
    if (value === null || value === undefined) return "-";
    const label = value > 0 ? "Over" : "Under";
    const sign = value > 0 ? "+" : "";
    return `${label} ${sign}${value.toFixed(1)}%`;
  };

  return (
    <div className="mtd-dashboard">
      <header className="mtd-header">
        <h1>
          <span>FFUN</span> - MTD Performance Report
        </h1>
        <div className="mtd-header-meta">
          <div>{dateLabel}</div>
          <div>Google Ads Only</div>
        </div>
      </header>
      <div className="mtd-container">
        <div className="mtd-controls">
          <label>Dealerships:</label>
          <div className="mtd-dropdown" ref={storeMenuRef}>
            <button
              type="button"
              className="mtd-dropdown-btn"
              onClick={() => setStoreMenuOpen((prev) => !prev)}
            >
              {storeLabel}
            </button>
            <div className={`mtd-dropdown-menu ${storeMenuOpen ? "open" : ""}`}>
              <div className="mtd-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setSelectedStores(new Set(aggregatedRows.map((row) => row.store)))}
                >
                  Select All
                </button>
                <button type="button" onClick={() => setSelectedStores(new Set())}>
                  Clear
                </button>
              </div>
              <div className="mtd-dropdown-divider" />
              <div className="mtd-dropdown-section">OEM Division</div>
              {storeGroups["OEM Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="mtd-dropdown-item" key={`oem-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
              <div className="mtd-dropdown-divider" />
              <div className="mtd-dropdown-section">Used Division</div>
              {storeGroups["Used Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="mtd-dropdown-item" key={`used-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>

          <label>Columns:</label>
          <div className="mtd-dropdown" ref={columnMenuRef}>
            <button
              type="button"
              className="mtd-dropdown-btn"
              onClick={() => setColumnMenuOpen((prev) => !prev)}
            >
              {columnLabel}
            </button>
            <div className={`mtd-dropdown-menu ${columnMenuOpen ? "open" : ""}`}>
              <div className="mtd-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setVisibleColumns(new Set(COLUMNS.map((col) => col.key)))}
                >
                  Show All
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleColumns(new Set(["name"]))}
                >
                  Hide All
                </button>
              </div>
              <div className="mtd-dropdown-divider" />
              {COLUMNS.filter((col) => col.key !== "name").map((col) => {
                const checked = visibleColumns.has(col.key);
                return (
                  <label className="mtd-dropdown-item" key={col.key}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(visibleColumns);
                        if (checked) {
                          next.delete(col.key);
                        } else {
                          next.add(col.key);
                        }
                        next.add("name");
                        setVisibleColumns(next);
                      }}
                    />
                    {col.label}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {error && <div className="mtd-error">{error}</div>}

        <div className="mtd-table-wrap">
          <table>
            <thead>
              <tr>
                {visibleColumnList.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={visibleColumnList.length} className="mtd-empty">
                    No data found.
                  </td>
                </tr>
              ) : (
                sections.map((section) => {
                  const sectionTotal = calculateTotals(section.rows);

                  return (
                    <React.Fragment key={section.key}>
                      {section.rows.length > 0 && (
                        <tr className="mtd-division">
                          <td colSpan={visibleColumnList.length}>{section.key}</td>
                        </tr>
                      )}
                      {section.rows.map((row) => (
                        <tr key={`${section.key}-${row.store}`}>
                          {visibleColumnList.map((col) => {
                            let value = "";
                            let className = "";

                            switch (col.key) {
                              case "name":
                                value = row.store;
                                className = "mtd-store";
                                break;
                              case "budget":
                                value = formatCurrency(row.budget);
                                break;
                              case "spent":
                                value = formatCurrency(row.spent);
                                break;
                              case "budgetPace":
                                value = formatPace(row.pacePercent);
                                className = paceClass(row.pacePercent);
                                break;
                              case "totalSpent":
                                value = formatCurrency(row.totalSpent);
                                break;
                              case "leadGoal":
                                value = formatNumberZero(row.leadGoal);
                                break;
                              case "leads":
                                value = formatNumberOne(row.leads);
                                break;
                              case "totalLeads":
                                value = formatNumberOne(row.totalLeads);
                                break;
                              case "leadsPercent":
                                value = formatPercent(row.leadsPercent);
                                className = leadPctClass(row.leadsPercent);
                                break;
                              case "cplGoal":
                                value = formatCurrency(row.cplGoal);
                                break;
                              case "cpl":
                                value = formatCurrency(row.cpl);
                                className = cplClass(row.cpl, row.cplGoal);
                                break;
                              case "callGoal":
                                value = formatNumberOne(row.callGoal);
                                break;
                              case "calls":
                                value = formatNumberOne(row.calls);
                                break;
                              case "callsPercent":
                                value = formatPercent(row.callsPercent);
                                className = leadPctClass(row.callsPercent);
                                break;
                              case "costPerCall":
                                value = formatCurrency(row.costPerCall);
                                break;
                              default:
                                value = formatNumber(getMetricValue(row, [col.key], "-"));
                            }

                            return (
                              <td key={`${row.store}-${col.key}`} className={className}>
                                {value}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {section.rows.length > 0 && (
                        <tr className="mtd-total">
                          {visibleColumnList.map((col) => {
                            let value = "";
                            let className = "";

                            switch (col.key) {
                              case "name":
                                value = section.key === "OEM DIVISION" ? "OEM Total" : "Used Total";
                                className = "mtd-store";
                                break;
                              case "budget":
                                value = formatCurrency(sectionTotal.budget);
                                break;
                              case "spent":
                                value = formatCurrency(sectionTotal.spent);
                                break;
                              case "budgetPace":
                                value = formatPace(sectionTotal.pacePercent);
                                className = paceClass(sectionTotal.pacePercent);
                                break;
                              case "totalSpent":
                                value = formatCurrency(sectionTotal.totalSpent);
                                break;
                              case "leadGoal":
                                value = formatNumberZero(sectionTotal.leadGoal);
                                break;
                              case "leads":
                                value = formatNumberOne(sectionTotal.leads);
                                break;
                              case "totalLeads":
                                value = formatNumberOne(sectionTotal.totalLeads);
                                break;
                              case "leadsPercent":
                                value = formatPercent(sectionTotal.leadsPercent);
                                className = leadPctClass(sectionTotal.leadsPercent);
                                break;
                              case "cplGoal":
                                value = formatCurrency(sectionTotal.cplGoal);
                                break;
                              case "cpl":
                                value = formatCurrency(sectionTotal.cpl);
                                className = cplClass(sectionTotal.cpl, sectionTotal.cplGoal);
                                break;
                              case "callGoal":
                                value = formatNumberOne(sectionTotal.callGoal);
                                break;
                              case "calls":
                                value = formatNumberOne(sectionTotal.calls);
                                break;
                              case "callsPercent":
                                value = formatPercent(sectionTotal.callsPercent);
                                className = leadPctClass(sectionTotal.callsPercent);
                                break;
                              case "costPerCall":
                                value = formatCurrency(sectionTotal.costPerCall);
                                break;
                              default:
                                value = formatNumber(getMetricValue(sectionTotal, [col.key], "-"));
                            }

                            return (
                              <td key={`${section.key}-total-${col.key}`} className={className}>
                                {value}
                              </td>
                            );
                          })}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
              {filteredRows.length > 0 && sections.every((section) => section.rows.length > 0) && (
                <tr className="mtd-grand-total">
                  {visibleColumnList.map((col) => {
                    const grandTotal = calculateTotals(filteredRows);
                    let value = "";

                    switch (col.key) {
                      case "name":
                        value = "Grand Total";
                        break;
                      case "budget":
                        value = formatCurrency(grandTotal.budget);
                        break;
                      case "spent":
                        value = formatCurrency(grandTotal.spent);
                        break;
                      case "budgetPace":
                        value = formatPace(grandTotal.pacePercent);
                        break;
                      case "totalSpent":
                        value = formatCurrency(grandTotal.totalSpent);
                        break;
                      case "leadGoal":
                        value = formatNumberZero(grandTotal.leadGoal);
                        break;
                      case "leads":
                        value = formatNumberOne(grandTotal.leads);
                        break;
                      case "totalLeads":
                        value = formatNumberOne(grandTotal.totalLeads);
                        break;
                      case "leadsPercent":
                        value = formatPercent(grandTotal.leadsPercent);
                        break;
                      case "cplGoal":
                        value = formatCurrency(grandTotal.cplGoal);
                        break;
                      case "cpl":
                        value = formatCurrency(grandTotal.cpl);
                        break;
                      case "callGoal":
                        value = formatNumberOne(grandTotal.callGoal);
                        break;
                      case "calls":
                        value = formatNumberOne(grandTotal.calls);
                        break;
                      case "callsPercent":
                        value = formatPercent(grandTotal.callsPercent);
                        break;
                      case "costPerCall":
                        value = formatCurrency(grandTotal.costPerCall);
                        break;
                      default:
                        value = formatNumber(getMetricValue(grandTotal, [col.key], "-"));
                    }

                    return <td key={`grand-${col.key}`}>{value}</td>;
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mtd-charts">
          <div className="mtd-chart-card">
            <h3>Goal vs Achieved Leads ({dateLabel})</h3>
            <div className="mtd-chart-container">
              <Bar data={leadsChartData} options={leadsChartOptions} />
            </div>
          </div>
          <div className="mtd-chart-card">
            <h3>CPL by Dealership</h3>
            <div className="mtd-chart-container">
              <Bar data={cplChartData} options={cplChartOptions} />
            </div>
          </div>
        </div>

        <div className="mtd-footer">
          Source: Google Ads API - pulled {moment().format("MMM D, YYYY")} | Goals from Supabase goals table ({startDate.format("MMMM YYYY")}) | Leads = form submission conversions (d2c_lead, fl_form_submission) | Budget Pace = ((Budget / {endDate.daysInMonth()}) x {elapsedDays} days) vs Spent | FFUN Group: no lead/call goals assigned
        </div>
      </div>
    </div>
  );
}
